import { Button, Checkbox, FileButton, Modal, Pagination, Table } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { MAX_UPLOAD_SIZE_MB } from '~/common/file/constants';
import TimeAgo from '~/components/time/TimeAgo';
import { useFileUpload } from '~/hooks/upload/useFileUpload';
import type { TestFile } from '~/server/api/routers/testFile';
import { api } from '~/trpc-extra/api';

export type SelectableFile = TestFile & {
  selected: boolean;
};

export function FileChooser({
  projectId,
  open,
  onFileSelect,
  onClose,
}: {
  projectId: string;
  open: boolean;
  onFileSelect: (selectedItems: SelectableFile[]) => void;
  onClose: () => void;
}) {
  const [page, setPage] = useState<number>(1);
  const take = 10;
  const { data: testFiles } = api.testFile.listTestFiles.useQuery({
    projectId,
    take: 10,
    skip: (page - 1) * take,
  });

  const availableFiles: SelectableFile[] = useMemo(
    () => testFiles?.items.map((file) => ({ ...file, selected: false })) ?? [],
    [testFiles],
  );

  const [selectedFiles, setSelectedFiles] = useState<Record<string, SelectableFile>>({});

  const setChecked = (file: TestFile, checked: boolean) => {
    setSelectedFiles((prevSelectedFiles) => ({ ...prevSelectedFiles, [file.id]: { ...file, selected: checked } }));
  };

  const onSubmit = () => {
    const selectedItems = Object.keys(selectedFiles)
      .filter((id) => selectedFiles[id].selected)
      .map((id) => selectedFiles[id]);
    onFileSelect(selectedItems);
    setSelectedFiles({});
  };

  const { uploadFile, error: uploadError, isUploading } = useFileUpload(projectId);

  return (
    <Modal
      opened={open}
      size="lg"
      title={<b>Choose files to upload</b>}
      onClose={onClose}
    >
      <div className="flex flex-col space-y-4">
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Selected</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>Uploaded Date</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {!!isUploading ?
              <Table.Tr>
                <Table.Td colSpan={3}>Uploading...</Table.Td>
              </Table.Tr>
            : null}
            {availableFiles.map((file) => (
              <Table.Tr
                key={file.id}
                bg={selectedFiles[file.id] ? 'var(--mantine-color-blue-light)' : undefined}
              >
                <Table.Td>
                  <Checkbox
                    checked={selectedFiles[file.id]?.selected}
                    onChange={(e) => setChecked(file, e.target.checked)}
                  />
                </Table.Td>
                <Table.Td>{file.name}</Table.Td>
                <Table.Td>
                  <TimeAgo time={file.createdAt} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {testFiles && testFiles?.total > take ?
          <Pagination
            total={Math.ceil(testFiles!.total / take)}
            value={page}
            onChange={setPage}
          />
        : null}

        <div>
          <div className="text-sm">Don&apos;t see your file?</div>
          <div className="space-x-4">
            <FileButton onChange={(file) => uploadFile(file)}>
              {(props) => (
                <Button
                  className="p-0"
                  variant="transparent"
                  {...props}
                >
                  Add file to project folder
                </Button>
              )}
            </FileButton>
            <span className="text-xs font-normal text-stone-700 dark:text-stone-400">
              Size limit: {MAX_UPLOAD_SIZE_MB}MB
            </span>
          </div>
          {uploadError ?
            <div className="flex space-x-12">
              <div className="flex flex-row text-red-800 dark:text-red-400">
                <IconAlertTriangle />
                {uploadError}
              </div>
            </div>
          : null}
        </div>

        <div className="text-right">
          <Button onClick={onSubmit}>Select files</Button>
        </div>
      </div>
    </Modal>
  );
}
